import {
  IssueCaseDoc,
  IssueCaseStatusCode,
  IssueTaskCategory,
  IssueTaskDoc,
  IssueTaskStatus,
} from '@gooduncles/gu-app-schema';
import { useEffect, useMemo, useState } from 'react';
import {
  IssueCaseAnyCategory,
  ISSUE_CASE_ACTIVE_STATUSES,
  ISSUE_TASK_ACTIVE_STATUSES,
} from 'src/constants/issue.constants';

import { formatDate } from 'src/utils/date-util';
import { observeIssueCases, observeIssueTasks } from 'src/utils/firebase/firebase-short-cut';

export interface RankingItem {
  id: string;
  name: string;
  count: number;
}

export interface IssueCaseSummary {
  total: number;
  byStatus: Record<IssueCaseStatusCode, number>;
  byCategory: Record<IssueCaseAnyCategory, number>;
  topProducts: RankingItem[];
  topStores: RankingItem[];
}

export interface IssueTaskSummary {
  total: number;
  byStatus: Record<IssueTaskStatus, number>;
  byCategory: Record<IssueTaskCategory, number>;
  topProducts: RankingItem[];
  topStores: RankingItem[];
}

const calculateCaseSummary = (cases: IssueCaseDoc[]): IssueCaseSummary => {
  const byStatus = cases.reduce(
    (acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    },
    {} as Record<IssueCaseStatusCode, number>,
  );

  const byCategory = cases.reduce(
    (acc, c) => {
      acc[c.category] = (acc[c.category] || 0) + 1;
      return acc;
    },
    {} as Record<IssueCaseAnyCategory, number>,
  );

  // 상품별 이슈 집계
  const productCounts = new Map<string, { name: string; count: number }>();
  cases.forEach((c) => {
    c.products?.forEach((p) => {
      const existing = productCounts.get(p.productId);
      if (existing) {
        existing.count += 1;
      } else {
        productCounts.set(p.productId, { name: p.productName, count: 1 });
      }
    });
  });

  const topProducts: RankingItem[] = Array.from(productCounts.entries())
    .map(([id, { name, count }]) => ({ id, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // 매장별 이슈 집계
  const storeCounts = new Map<string, { name: string; count: number }>();
  cases.forEach((c) => {
    const existing = storeCounts.get(c.storeId);
    if (existing) {
      existing.count += 1;
    } else {
      storeCounts.set(c.storeId, { name: c.storeName, count: 1 });
    }
  });

  const topStores: RankingItem[] = Array.from(storeCounts.entries())
    .map(([id, { name, count }]) => ({ id, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    total: cases.length,
    byStatus,
    byCategory,
    topProducts,
    topStores,
  };
};

const calculateTaskSummary = (tasks: IssueTaskDoc[]): IssueTaskSummary => {
  const byStatus = tasks.reduce(
    (acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    },
    {} as Record<IssueTaskStatus, number>,
  );

  const byCategory = tasks.reduce(
    (acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + 1;
      return acc;
    },
    {} as Record<IssueTaskCategory, number>,
  );

  // 상품별 태스크 집계
  const productCounts = new Map<string, { name: string; count: number }>();
  tasks.forEach((t) => {
    t.products?.forEach((p) => {
      const existing = productCounts.get(p.productId);
      if (existing) {
        existing.count += 1;
      } else {
        productCounts.set(p.productId, { name: p.productName, count: 1 });
      }
    });
  });

  const topProducts: RankingItem[] = Array.from(productCounts.entries())
    .map(([id, { name, count }]) => ({ id, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // 매장별 태스크 집계
  const storeCounts = new Map<string, { name: string; count: number }>();
  tasks.forEach((t) => {
    const existing = storeCounts.get(t.storeId);
    if (existing) {
      existing.count += 1;
    } else {
      storeCounts.set(t.storeId, { name: t.storeName, count: 1 });
    }
  });

  const topStores: RankingItem[] = Array.from(storeCounts.entries())
    .map(([id, { name, count }]) => ({ id, name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    total: tasks.length,
    byStatus,
    byCategory,
    topProducts,
    topStores,
  };
};

interface UseIssueCaseHistoryDataOptions {
  enabled: boolean;
  startDate: string;
  endDate: string;
}

export const useIssueCaseHistoryData = ({ enabled, startDate, endDate }: UseIssueCaseHistoryDataOptions) => {
  const [cases, setCases] = useState<IssueCaseDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setCases([]);
      return;
    }

    setIsLoading(true);

    const formattedStartDate = formatDate(new Date(startDate), "yyyy-MM-dd'T'00:00:00+0900");
    const formattedEndDate = formatDate(new Date(endDate), "yyyy-MM-dd'T'23:59:59+0900");

    const subscription = observeIssueCases([
      ['registeredAt', '>=', formattedStartDate],
      ['registeredAt', '<=', formattedEndDate],
    ]).subscribe({
      next: (data) => {
        // CANCELED, DELETED 상태 제외
        const filteredData = data.filter((c) => ISSUE_CASE_ACTIVE_STATUSES.includes(c.status));
        // 등록일 내림차순 정렬
        const sortedData = filteredData.sort((a, b) => b.registeredAt.localeCompare(a.registeredAt));
        setCases(sortedData);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('Failed to fetch issue cases', err);
        setIsLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [enabled, startDate, endDate]);

  const summary = useMemo(() => calculateCaseSummary(cases), [cases]);

  return { cases, summary, isLoading };
};

interface UseIssueTaskHistoryDataOptions {
  enabled: boolean;
  startDate: string;
  endDate: string;
}

export const useIssueTaskHistoryData = ({ enabled, startDate, endDate }: UseIssueTaskHistoryDataOptions) => {
  const [tasks, setTasks] = useState<IssueTaskDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setTasks([]);
      return;
    }

    setIsLoading(true);

    const formattedStartDate = formatDate(new Date(startDate), "yyyy-MM-dd'T'00:00:00+0900");
    const formattedEndDate = formatDate(new Date(endDate), "yyyy-MM-dd'T'23:59:59+0900");

    const subscription = observeIssueTasks([
      ['registeredAt', '>=', formattedStartDate],
      ['registeredAt', '<=', formattedEndDate],
    ]).subscribe({
      next: (data) => {
        // CANCELED, DELETED 상태 제외
        const filteredData = data.filter((t) => ISSUE_TASK_ACTIVE_STATUSES.includes(t.status));
        // 등록일 내림차순 정렬
        const sortedData = filteredData.sort((a, b) => b.registeredAt.localeCompare(a.registeredAt));
        setTasks(sortedData);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('Failed to fetch issue tasks', err);
        setIsLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [enabled, startDate, endDate]);

  const summary = useMemo(() => calculateTaskSummary(tasks), [tasks]);

  return { tasks, summary, isLoading };
};
